import React, { useEffect } from 'react';
import { FormGroup as BootstrapFormGroup, Label } from 'reactstrap';
import { 
    DeepRequired, 
    FieldErrorsImpl, 
    FieldValues, 
    UseFormRegister, 
    UseFormTrigger, 
    UseFormWatch, 
    Path 
} from 'react-hook-form';
import _ from 'lodash';
import type { DeepMap, DeepPartial } from "react-hook-form";
import classnames from 'classnames';

import { Input, Textarea } from './Input';
import FormError from '../FormError';

export interface FormInputProps<T extends FieldValues> {
    label: string;
    register: UseFormRegister<T>;
    name: Path<T>;  // ✅ Ensure `name` is correctly typed
    errors: FieldErrorsImpl<DeepRequired<T>>;
    touchedFields?: Partial<Readonly<DeepMap<DeepPartial<T>, boolean>>>;
    trigger?: UseFormTrigger<T>;
    watch?: UseFormWatch<T>;
    type?: 'text' | 'number' | 'date' | 'textarea' | 'password';
    normalCase?: boolean;
    disabled?: boolean;
    index?: number; // ✅ For handling field arrays
}

export default function FormInput<T extends FieldValues>({
    label,
    register,
    name,
    errors,
    touchedFields,
    trigger,
    watch,
    type,
    normalCase = true,
    disabled,
    index,
}: FormInputProps<T>) {
    // Construct the correct field path for array fields
    const fieldName = index !== undefined ? `${name}[${index}]` as Path<T> : name;

    // Use lodash to safely extract the error message for deep paths
    const error = _.get(errors, fieldName)?.message as string | undefined;

    // ✅ Fix Type Error: Ensure `watch` gets a properly typed Path<T>
    const value = watch ? watch(fieldName as Path<T>) : undefined;

    const classes = classnames({ 'normal-text': normalCase });

    // ✅ Fix Type Error: Ensure `trigger` gets a properly typed Path<T>
    useEffect(() => {
        if (trigger) {
            trigger(fieldName as Path<T>);
        }
    }, [value, trigger, fieldName]);

    return (
        <BootstrapFormGroup>
            <Label>
                {label}{' '}
                <FormError message={error} />
            </Label>
            {type === 'textarea' ? (
                <Textarea
                    {...register(fieldName)}
                    error={Boolean(error)}
                    normalCase={normalCase}
                    disabled={disabled}
                />
            ) : (
                <Input
                    type={type || 'text'}
                    {...register(fieldName)}
                    error={Boolean(error)}
                    normalCase={normalCase}
                    disabled={disabled}
                />
            )}
        </BootstrapFormGroup>
    );
}
